import { customRef, type Ref } from 'vue';

/**
 * Selector for focusable elements.
 */
const focusableElementsSelector = [
  'button:not(:disabled)',
  'a[href]',
  'area[href]',
  'input:not(:disabled):not([type="hidden"])',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not(:disabled)',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ');

const isFocusable = (element: HTMLElement): boolean =>
  !element.closest('[hidden], [inert], fieldset:disabled') &&
  (element.checkVisibility?.({ visibilityProperty: true }) ?? true);

/**
 * Hook for creating a focus trap within a specified DOM element.
 * This trap ensures that the keyboard focus remains within the specified element.
 *
 * @param firstSelectionOnLastItem - Whether to select the first focusable element on the last focusable element.
 * @returns An object containing `trapRef`, a Vue ref that should be bound to the element to trap focus within.
 */
export const useFocusTrap = (firstSelectionOnLastItem = true) => {
  let firstFocusable: HTMLElement | null = null;
  let lastFocusable: HTMLElement | null = null;

  const updateFocusableElements = (): void => {
    const focusableElements = trapRef.value
      ? Array.from(
          trapRef.value.querySelectorAll<HTMLElement>(
            focusableElementsSelector,
          ),
        ).filter(isFocusable)
      : [];
    firstFocusable = focusableElements[0] ?? null;
    lastFocusable = focusableElements[focusableElements.length - 1] ?? null;
  };

  /**
   * Custom Vue ref used to track and manage the focus trap element.
   */
  const trapRef: Ref<HTMLElement | null> = customRef((track, trigger) => {
    let trapEl: HTMLElement | null = null;
    return {
      get(): HTMLElement | null {
        track();
        return trapEl;
      },
      set(value: HTMLElement | null): void {
        trapEl = value;
        if (value) {
          initFocusTrap();
        } else {
          clearFocusTrap();
        }
        trigger();
      },
    };
  });

  /**
   * Handler for keyboard events. Used to manage focus within the trap.
   *
   * @param e - The keyboard event.
   */
  const keyHandler = (e: KeyboardEvent): void => {
    const isTabPressed: boolean = e.key === 'Tab';

    if (!isTabPressed) return;

    updateFocusableElements();

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable?.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable?.focus();
        e.preventDefault();
      }
    }
  };

  /**
   * Initializes the focus trap by setting up event listeners and focusing the first element.
   */
  const initFocusTrap = (): void => {
    if (!trapRef.value) return;
    updateFocusableElements();
    document.addEventListener('keydown', keyHandler);
    if (firstSelectionOnLastItem) {
      lastFocusable?.focus();
    } else {
      firstFocusable?.focus();
    }
  };

  /**
   * Clears the focus trap by removing event listeners.
   */
  const clearFocusTrap = (): void => {
    document.removeEventListener('keydown', keyHandler);
  };

  return {
    trapRef,
    initFocusTrap,
    clearFocusTrap,
  };
};
